import { useEffect, useState } from 'react';
import { safeLocalStorage, safeSessionStorage } from './safeStorage';

/**
 * Custom hook to detect different types of user sessions:
 * 1. First ever visit: Neither localStorage nor sessionStorage has the key
 * 2. New session: localStorage has the key but sessionStorage doesn't (user closed and reopened browser)
 * 3. Existing session: Both localStorage and sessionStorage have the key
 * 
 * This helps distinguish between completely new users, returning users in a new session,
 * and users continuing their existing session.
 */
const useIsNewSession = (key: string) => {
  const [isNewSession, setIsNewSession] = useState(false);

  const establishSession = () => {
    const isFirstView = !safeLocalStorage.getItem(key) && !safeSessionStorage.getItem(key);

    if (!isFirstView) {
      return;
    }

    safeLocalStorage.setItem(key, 'true');
    safeSessionStorage.setItem(key, 'true');
  };

  const checkSession = () => {
    const hasLocalStorage = Boolean(safeLocalStorage.getItem(key));
    const hasSessionStorage = Boolean(safeSessionStorage.getItem(key));

    // New session is when we have a record in localStorage (returning user)
    // but not in sessionStorage (browser was closed and reopened)
    setIsNewSession(hasLocalStorage && !hasSessionStorage);
  };

  useEffect(() => {
    establishSession();
    checkSession();
  }, []);

  return isNewSession;
};

export default useIsNewSession;
